import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, GitCommit, History, ListTodo, Timer } from 'lucide-react'
import type { Activiteit, Commit, Taak } from '../../gedeeld/types'
import { api, github } from '../lib/api'
import { eersteRegel, geleden, isTeLaat, meervoud, opDringendheid, sleutelVan } from '../lib/opmaak'
import { Link, useTitel } from '../lib/router'
import { openTaak, useBasis, useStaat, useTaakWijzigingen, verwerk } from '../lib/staat'
import { Avatar, Laden, Leeg, Punt, Voortgang } from './ui'
import { Deadline, PrioriteitIcoon, SoortIcoon, StatusPil } from './taak/stukjes'

/* ===========================================================================
 *  Het begin van de dag
 *
 *  Wat er op jouw naam staat, wat er loopt, en wat er sinds gisteren
 *  gebeurde. Geen grafieken om de grafieken: elk getal hier is iets waar je
 *  op kunt klikken om er iets aan te doen.
 * =========================================================================== */

export default function Overzicht() {
  useTitel('Overzicht', 'Wat er voor jou klaarstaat')
  const ik = useStaat((s) => s.ik)!
  const githubAan = useStaat((s) => s.github.ingesteld)
  const { projecten, geladen } = useBasis()
  const [mijn, setMijn] = useState<Taak[] | null>(null)
  const [activiteit, setActiviteit] = useState<Activiteit[]>([])
  const [commits, setCommits] = useState<Commit[] | null>(null)

  useEffect(() => {
    api.taken({ mijn: true }).then(setMijn).catch(() => setMijn([]))
    api.activiteit({ limiet: 25 }).then((x) => setActiviteit(x.activiteit)).catch(() => setActiviteit([]))
  }, [])

  useEffect(() => {
    if (!githubAan) return
    github.changelog(7).then((x) => setCommits(x.commits.slice(0, 10))).catch(() => setCommits([]))
  }, [githubAan])

  useTaakWijzigingen(useCallback((t) => {
    setMijn((x) => (x ? verwerk(x, t, (y) => y.toegewezenAan === ik.id) : x))
  }, [ik.id]))

  const open = useMemo(() => (mijn ?? []).filter((t) => t.status !== 'klaar').sort(opDringendheid), [mijn])
  const teLaat = open.filter(isTeLaat).length
  const weekKlaar = (mijn ?? []).filter((t) => t.klaarAt && t.klaarAt > Date.now() - 7 * 86400000).length
  const sprints = projecten.filter((p) => p.sprintActief && !p.archief)
  const lopend = projecten.filter((p) => !p.archief && p.status === 'actief')

  const uur = new Date().getHours()
  const groet = uur < 12 ? 'Goedemorgen' : uur < 18 ? 'Goedemiddag' : 'Goedenavond'
  const voornaam = ik.naam.split(' ')[0]

  return (
    <div className="pagina">
      <header className="pagina-kop">
        <div>
          <h1>{groet}, {voornaam}</h1>
          <p className="stil">{new Date().toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        </div>
      </header>

      {/* De cijfertegels van het dashboard (Stat daar): label, getal, en een
          getint icoontje rechtsboven. */}
      <div className="tegels">
        <Link naar="/mijn" className="tegel">
          <span className="tegel-label">Open op jouw naam</span>
          <span className="tegel-getal">{mijn ? open.length : '–'}</span>
          <span className="tegel-sub">in alle projecten en het dashboard</span>
          <span className="tegel-glyph t-brand"><ListTodo size={19} /></span>
        </Link>
        <Link naar="/mijn?filter=laat" className="tegel">
          <span className="tegel-label">Te laat</span>
          <span className={`tegel-getal ${teLaat ? 'gevaar' : ''}`}>{mijn ? teLaat : '–'}</span>
          <span className="tegel-sub">{teLaat ? 'deadline is voorbij' : 'alles op tijd'}</span>
          <span className={`tegel-glyph ${teLaat ? 't-danger' : 't-ok'}`}><AlertTriangle size={19} /></span>
        </Link>
        <div className="tegel">
          <span className="tegel-label">Deze week afgerond</span>
          <span className="tegel-getal">{mijn ? weekKlaar : '–'}</span>
          <span className="tegel-sub">laatste 7 dagen</span>
          <span className="tegel-glyph t-ok"><CheckCircle2 size={19} /></span>
        </div>
        <Link naar="/projecten" className="tegel">
          <span className="tegel-label">Lopende sprints</span>
          <span className="tegel-getal">{geladen ? sprints.length : '–'}</span>
          <span className="tegel-sub">{lopend.length} {lopend.length === 1 ? 'project' : 'projecten'} actief</span>
          <span className="tegel-glyph t-info"><Timer size={19} /></span>
        </Link>
      </div>

      <div className="twee-kolom">
        <section className="vlak">
          <div className="vlak-kop">
            <h2>Eerst dit</h2>
            <Link naar="/mijn" className="vlak-link">Alles</Link>
          </div>
          {!mijn ? <Laden /> : !open.length ? (
            <Leeg titel="Niets op jouw naam" tekst="Alles is af, of er is nog niets aan je gegeven." />
          ) : (
            <div className="regels">
              {open.slice(0, 9).map((t) => (
                <button key={t.id} type="button" className="regel" onClick={() => openTaak(t.id)}>
                  <SoortIcoon soort={t.soort} />
                  <PrioriteitIcoon p={t.prioriteit} />
                  <span className="mono stil regel-sleutel">{sleutelVan(t, projecten) ?? ''}</span>
                  <span className="regel-titel">{t.titel}</span>
                  <Deadline taak={t} />
                  <StatusPil status={t.status} />
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="vlak">
          <div className="vlak-kop"><h2>Lopende sprints</h2></div>
          {!geladen ? <Laden /> : !sprints.length ? (
            <Leeg titel="Geen sprint bezig" tekst="Start er een in de backlog van een project." />
          ) : (
            <div className="sprint-lijst">
              {sprints.map((p) => {
                const s = p.sprintActief!
                const dagenOver = s.eindAt ? Math.ceil((s.eindAt - Date.now()) / 86400000) : null
                return (
                  <Link key={p.id} naar={`/p/${p.id}/backlog`} className="sprint-regel">
                    <span className="sprint-regel-kop">
                      <Punt kleur={p.kleur} /><strong>{p.naam}</strong><span className="stil">{s.naam}</span>
                      <span className="vul" />
                      {dagenOver !== null && (
                        <span className={dagenOver < 0 ? 'gevaar' : 'stil'}>
                          {dagenOver < 0 ? `${-dagenOver} dagen over tijd` : dagenOver === 0 ? 'eindigt vandaag' : `nog ${meervoud(dagenOver, 'dag', 'dagen')}`}
                        </span>
                      )}
                    </span>
                    {s.doel && <span className="stil sprint-doel">{s.doel}</span>}
                  </Link>
                )
              })}
            </div>
          )}
        </section>
      </div>

      <section className="vlak">
        <div className="vlak-kop">
          <h2>Projecten</h2>
          <Link naar="/projecten" className="vlak-link">Alle projecten</Link>
        </div>
        {!geladen ? <Laden /> : !lopend.length ? (
          <Leeg titel="Nog geen lopende projecten">
            <Link naar="/projecten?nieuw=1" className="knop k-hoofd">Een project beginnen</Link>
          </Leeg>
        ) : (
          <div className="project-raster">
            {lopend.slice(0, 8).map((p) => {
              const t = p.telling!
              return (
                <Link key={p.id} naar={`/p/${p.id}/bord`} className={`project-tegel t-${p.kleur}`}>
                  <span className="project-tegel-kop">
                    <span className="project-sleutel">{p.sleutel ?? '—'}</span>
                    <strong>{p.naam}</strong>
                  </span>
                  <Voortgang klaar={t.klaar} totaal={t.totaal} kleur={p.kleur} />
                  <span className="project-tegel-voet stil">
                    <span>{t.open} open</span>
                    {t.teLaat > 0 && <span className="gevaar">{t.teLaat} te laat</span>}
                    <span className="vul" />
                    {t.laatst && <span>{geleden(t.laatst)}</span>}
                  </span>
                </Link>
              )
            })}
          </div>
        )}
      </section>

      <div className="twee-kolom">
        <section className="vlak">
          <div className="vlak-kop"><h2><History size={15} /> Wat er gebeurde</h2></div>
          {!activiteit.length ? <p className="stil">Nog niets, of het logboek is even niet bereikbaar.</p> : (
            <div className="geschiedenis">
              {activiteit.map((r) => {
                const p = projecten.find((x) => x.id === r.projectId)
                return (
                  <div key={r.id} className="geschiedenis-regel">
                    <Avatar naam={r.wieNaam} id={r.wie} grootte={20} />
                    <span>
                      <strong>{r.wieNaam}</strong>{' '}
                      {r.taakId
                        ? <button type="button" className="link-knop" onClick={() => openTaak(r.taakId!)}>{r.tekst}</button>
                        : r.tekst}
                      {p && <span className="stil"> · {p.naam}</span>}
                    </span>
                    <span className="vul" />
                    <span className="stil">{geleden(r.at)}</span>
                  </div>
                )
              })}
            </div>
          )}
        </section>

        <section className="vlak">
          <div className="vlak-kop">
            <h2><GitCommit size={15} /> Laatste commits</h2>
            {githubAan && <Link naar="/changelog" className="vlak-link">Changelog</Link>}
          </div>
          {!githubAan ? (
            <p className="stil">GitHub is nog niet gekoppeld. Zodra er een token op de Worker staat, verschijnen hier de commits van alle repo's.</p>
          ) : !commits ? <Laden /> : !commits.length ? <p className="stil">Deze week nog niets gepusht.</p> : (
            <div className="commit-lijst klein">
              {commits.map((c) => (
                <Link key={c.repo + c.sha} naar={`/code/${c.repo}/commit/${c.sha}`} className="commit-regel">
                  <Avatar naam={c.auteur.naam} url={c.auteur.avatar} grootte={20} />
                  <span className="commit-bericht">{eersteRegel(c.bericht)}</span>
                  <span className="stil">{c.repo}</span>
                  <span className="stil">{geleden(c.datum)}</span>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
